import { useLingui } from '@lingui/react/macro';
import { Wallet, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { FlowPanel, type FlowPanelStep } from '@/shared/flow';
import { ChoiceCard, ChoiceCardGroup } from '@/shared/ui/choice-card';

// As peças da consulta de margem (família E do Fluxo guiado, U53): o painel em fases (consulta → autorização → seleção
// ou restrições) sobre o `FlowPanel` de `shared`, e os cartões das margens como `ChoiceCard` (o `CardsSelect` com
// `isCheckbox` do legado, SelectWarranty.tsx:91-96). O legado empilhava um drawer por etapa.

export interface MarginPanelProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description?: string | undefined;
  /** As fases do mini-trilho; sem elas, o painel simples (os detalhes da aba Crédito). */
  readonly steps?: readonly FlowPanelStep[] | undefined;
  readonly current?: string | undefined;
  /** O "Voltar" da fase; sem ele, a saída (`dismissLabel`, padrão "Fechar"; `null` esconde). */
  readonly onBack?: (() => void) | undefined;
  readonly dismissLabel?: string | null | undefined;
  /** Os botões da fase, o principal por último. */
  readonly footer?: ReactNode;
  readonly busy?: boolean | undefined;
  readonly children: ReactNode;
}

/** Painel lateral na web e bottom sheet no iPhone, com o mini-trilho das fases e o rodapé fixo. */
export function MarginPanel({ open, onOpenChange, title, description, steps, current, onBack, dismissLabel, footer, busy, children }: MarginPanelProps) {
  return (
    <FlowPanel
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      steps={steps}
      current={current}
      onBack={onBack}
      dismissLabel={steps ? dismissLabel : (dismissLabel ?? null)}
      actions={footer}
      busy={busy}
      slot="margin-panel"
    >
      {children}
    </FlowPanel>
  );
}

export interface MarginCardOption {
  readonly key: string;
  readonly title: string;
  /** "Matrícula: X | Data da consulta: D | Valor da margem: R$" (Summary/Zetra.tsx:22-26, 82-84). */
  readonly description: string;
  /** O tooltip "Informações" do legado (TooltipZetra.tsx), aberto por clique. */
  readonly details: readonly (readonly [string, string])[];
  /** O `disableItem` do `CardsSelect` (o limite da Consignação para compra). */
  readonly disabled?: boolean | string | undefined;
}

/**
 * Os cartões das margens: `ChoiceCard` múltiplo (role=checkbox, como o `isCheckbox` do legado); `single` marca uma por
 * vez e deixa desmarcar (SelectWarranty.tsx:63-76). O "Informações" fica fora do card (botão dentro de botão não vale).
 */
export function MarginCards({
  options,
  selected,
  onToggle,
  label,
  detailsLabel,
  single = false,
  icon = Wallet,
}: {
  readonly options: readonly MarginCardOption[];
  readonly selected: readonly string[];
  readonly onToggle: (key: string, checked: boolean) => void;
  readonly label: string;
  readonly detailsLabel?: string;
  readonly single?: boolean;
  readonly icon?: LucideIcon;
}) {
  const { t } = useLingui();
  if (options.length === 0) return null;
  const change = (next: string[]) => {
    const added = next.find((key) => !selected.includes(key));
    if (added !== undefined) {
      if (single) for (const key of selected) onToggle(key, false);
      onToggle(added, true);
      return;
    }
    const removed = selected.find((key) => !next.includes(key));
    if (removed !== undefined) onToggle(removed, false);
  };
  return (
    <ChoiceCardGroup multiple aria-label={label} value={[...selected]} onValueChange={change} className="grid-cols-1">
      {options.map((option) => (
        <div key={option.key} className="flex flex-col gap-1.5">
          <ChoiceCard
            value={option.key}
            icon={icon}
            disabled={option.disabled}
            // O nome acessível leva a descrição, como o `label` do legado: os cartões do mesmo titular só se distinguem
            // pela matrícula ou pelo benefício.
            title={
              <>
                <span className="block text-sm break-words">{option.title}</span>
                <span className="mt-0.5 block text-xs font-normal break-words text-muted-foreground group-aria-checked/choice:text-foreground">{option.description}</span>
              </>
            }
          />
          {option.details.length > 0 ? (
            <details className="px-1 text-xs">
              <summary className="cursor-pointer font-medium text-muted-foreground">{detailsLabel ?? t`Informações`}</summary>
              <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                {option.details.map(([term, value]) => (
                  <div key={term} className="contents">
                    <dt className="text-muted-foreground">{term}</dt>
                    <dd className="break-words">{value}</dd>
                  </div>
                ))}
              </dl>
            </details>
          ) : null}
        </div>
      ))}
    </ChoiceCardGroup>
  );
}
